"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getSyncNoticeState = exports.reorderOverviewLayout = exports.getCardRowValue = exports.computeCardsPerRow = exports.filterOverviewLayout = exports.normalizeOverviewPageLayout = exports.overviewLayoutsMatch = exports.getEntityShareFlags = void 0;
const immutable_1 = require("immutable");
const luxon_1 = require("luxon");
const getEntityShareFlags = (isEntityShareEnableUser, accountTypes = [], bankingAccountTypes = []) => ({
    isOnlyInvestment: Boolean(isEntityShareEnableUser && accountTypes.every((type) => ['INVESTMENT'].includes(type))),
    isOnlyBank: Boolean(isEntityShareEnableUser && accountTypes.every((type) => bankingAccountTypes.includes(type))),
});
exports.getEntityShareFlags = getEntityShareFlags;
const overviewLayoutsMatch = (defaultLayout, prefLayout) => {
    if (defaultLayout.length !== prefLayout.size) {
        return false;
    }
    for (let i = 0; i < defaultLayout.length; i += 1) {
        const currentCardKey = defaultLayout[i].key;
        if (!prefLayout.find((prefCard) => currentCardKey === prefCard.key)) {
            return false;
        }
    }
    return true;
};
exports.overviewLayoutsMatch = overviewLayoutsMatch;
const normalizeOverviewPageLayout = ({ prefLayout, defaultLayout, createCardRecord, }) => {
    // Older preference payloads stored non-object entries, so reset them to the current layout shape.
    if (typeof prefLayout.first() !== 'object') {
        return {
            changed: true,
            layout: (0, immutable_1.List)(defaultLayout),
        };
    }
    const normalizedLayout = prefLayout.map((card) => {
        var _a, _b;
        return createCardRecord({
            key: ((_a = card === null || card === void 0 ? void 0 : card.get) === null || _a === void 0 ? void 0 : _a.call(card, 'key')) || (card === null || card === void 0 ? void 0 : card.key),
            value: Boolean((card === null || card === void 0 ? void 0 : card.value) || ((_b = card.get) === null || _b === void 0 ? void 0 : _b.call(card, 'value'))),
        });
    });
    if ((0, exports.overviewLayoutsMatch)(defaultLayout, normalizedLayout)) {
        return {
            changed: false,
            layout: normalizedLayout,
        };
    }
    let hasDuplicates = false;
    // Remove duplicate cards before validating the final layout size.
    let dedupedLayout = (0, immutable_1.List)(normalizedLayout.reduce((arr, card) => {
        if (arr.find((datum) => datum.key === card.key)) {
            hasDuplicates = true;
        }
        else {
            arr.push(card);
        }
        return arr;
    }, []));
    if (defaultLayout.length !== dedupedLayout.size) {
        dedupedLayout = (0, immutable_1.List)(defaultLayout);
    }
    return {
        changed: hasDuplicates || defaultLayout.length !== dedupedLayout.size || dedupedLayout !== normalizedLayout,
        layout: dedupedLayout,
    };
};
exports.normalizeOverviewPageLayout = normalizeOverviewPageLayout;
const filterOverviewLayout = ({ filteredLayout, overviewLayoutEntity, isEntityShareEnableUser, isOnlyBank, isOnlyInvestment, }) => {
    let layout = (0, immutable_1.List)();
    filteredLayout.forEach((nav) => {
        const navItem = overviewLayoutEntity.find(({ key }) => key === nav.key);
        if ((!(navItem === null || navItem === void 0 ? void 0 : navItem.hideOnEntityShare) || !isEntityShareEnableUser)
            && (!(navItem === null || navItem === void 0 ? void 0 : navItem.hideOnEntityShareOnlyBanking) || !isOnlyBank)
            && (!(navItem === null || navItem === void 0 ? void 0 : navItem.hideOnEntityShareOnlyInvestment) || !isOnlyInvestment)) {
            layout = layout.push(nav);
        }
    });
    return layout;
};
exports.filterOverviewLayout = filterOverviewLayout;
const computeCardsPerRow = (width, breakpoints) => {
    if (width < breakpoints.md) {
        return 1;
    }
    if (width < breakpoints.lg) {
        return 1;
    }
    if (width < breakpoints.xl) {
        return 2;
    }
    return 3;
};
exports.computeCardsPerRow = computeCardsPerRow;
const getCardRowValue = (cardId, cardsPerRow, cardsConfig) => {
    var _a, _b, _c, _d, _e, _f;
    const cardObj = cardsConfig[cardId.key];
    if (!cardObj) {
        return 4;
    }
    switch (cardsPerRow) {
        case 3:
            return (_c = (_b = (_a = cardObj.xl) !== null && _a !== void 0 ? _a : cardObj.lg) !== null && _b !== void 0 ? _b : cardObj.xs) !== null && _c !== void 0 ? _c : 4;
        case 2:
            return (_e = (_d = cardObj.lg) !== null && _d !== void 0 ? _d : cardObj.xs) !== null && _e !== void 0 ? _e : 4;
        case 1:
            return (_f = cardObj.xs) !== null && _f !== void 0 ? _f : 4;
        default:
            return 4;
    }
};
exports.getCardRowValue = getCardRowValue;
const reorderOverviewLayout = ({ currentLayout, fromCard, toCard, }) => {
    let newList = currentLayout;
    if (fromCard === toCard) {
        return currentLayout;
    }
    // Remove the dragged card first, then reinsert it relative to the destination card.
    const fromIndex = newList.indexOf(fromCard);
    const initialToIndex = newList.indexOf(toCard);
    newList = newList.filter((x) => x !== fromCard);
    let toIndex = newList.indexOf(toCard);
    if (fromIndex <= initialToIndex) {
        toIndex += 1;
    }
    newList = newList.splice(toIndex, 0, fromCard);
    return newList;
};
exports.reorderOverviewLayout = reorderOverviewLayout;
const getSyncNoticeState = ({ lastSyncId, isWebFirstDataset = false, showLastDesktopSyncInfo = true, now = luxon_1.DateTime.now(), }) => {
    var _a;
    const lastSyncDate = lastSyncId ? luxon_1.DateTime.fromISO(lastSyncId) : null;
    const lastSyncValid = (_a = lastSyncDate === null || lastSyncDate === void 0 ? void 0 : lastSyncDate.isValid) !== null && _a !== void 0 ? _a : false;
    const daysSinceLastSync = lastSyncValid ? Math.max(0, Math.floor(now.diff(lastSyncDate, 'days').days)) : null;
    const canShowSyncNotice = Boolean(lastSyncValid && daysSinceLastSync !== null && daysSinceLastSync >= 10 && !isWebFirstDataset);
    const shouldShowSyncNotice = Boolean(canShowSyncNotice && showLastDesktopSyncInfo !== false);
    const highlightSyncNotice = Boolean(shouldShowSyncNotice && daysSinceLastSync >= 30);
    const lastSyncLabel = lastSyncValid && lastSyncDate ? lastSyncDate.toLocaleString({
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }) : '';
    return {
        canShowSyncNotice,
        daysSinceLastSync,
        highlightSyncNotice,
        lastSyncDate,
        lastSyncLabel,
        lastSyncValid,
        shouldShowSyncNotice,
    };
};
exports.getSyncNoticeState = getSyncNoticeState;
